import { ActionTree, Commit } from 'vuex';
import { getUserPreferences } from '@/features/settings/services/preferences.services';
import {
  hideAnimation, redirectTo, redirectToSignIn,
} from '@/features/common/helpers';
import { getToken, routesAvailableWithoutAuth } from '@/features/auth/helpers';
import { addClarityCustomTags } from '@/features/common/plugins/clarity';
import { NotificationData } from '@/features/settings/types';
import { UserInfo } from '@/features/users/types';
import { checkLegacyAuthStatus } from '@/features/auth/services/auth.services';
import { RootState } from '../types';
import { fetchUserInfo, getCountriesData } from '../services/common.services';

const isPathIncludes = (path: string): boolean => {
  const { pathname } = window.location;
  return pathname.includes(path);
};

export const hideAllAnimations = (): void => {
  hideAnimation('welcome-back-animation', 'greeting');
  hideAnimation('onboarding-animation', 'onboarding', 2000);
};

export const isPasswordResetPage = (): boolean => {
  return isPathIncludes('/set-password') || isPathIncludes('/verify-user');
};

export const isTermsAndConditionsPage = (): boolean => {
  return isPathIncludes('/terms');
};

export const redirectUser = (userInfo: UserInfo): boolean => {
  if (isPasswordResetPage()) return true;
  if (isTermsAndConditionsPage()) return true;

  const { isDefaultAddressRequired, isSurveyRequired } = userInfo;

  if (isDefaultAddressRequired && !isSurveyRequired) {
    redirectTo(`/sign-up/about-me?addr-only=true&username=${encodeURIComponent(userInfo.username)}`, true);
    return true;
  }

  if (isDefaultAddressRequired) {
    redirectTo(`/sign-up/who-shipping?username=${encodeURIComponent(userInfo.username)}`, true);
    return true;
  }

  if (isSurveyRequired) {
    redirectTo(
      `/sign-up/who-shipping?username=${encodeURIComponent(userInfo.username)}&surveyOnly=true&onboarding=true`,
      true,
    );
    return true;
  }

  return false;
};

const fetchAndCommitUsersData = async (commit: Commit): Promise<boolean> => {
  let isRedirect = false;

  const userInfo = await fetchUserInfo();
  if (!userInfo) return isRedirect;

  isRedirect = redirectUser(userInfo);

  commit('setUserInfo', userInfo);
  return isRedirect;
};

const fetchAndCommitCountriesData = async (commit: Commit) => {
  const countriesRes = await getCountriesData();
  commit('setCountries', countriesRes);
};

export const fetchAndCommitUserPrefsData = async (commit: Commit) => {
  const userPrefRes = await getUserPreferences();
  commit('setUserPreferences', userPrefRes);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const actions: ActionTree<RootState, any> = {
  async init({ dispatch }) {
    const isAuth = !!getToken();
    // const isLegacyLoggedIn = await checkLegacyAuthStatus();
    // if (isAuth && isLegacyLoggedIn) {
    if (isAuth) {
      await dispatch('initFetch');
    } else if (!routesAvailableWithoutAuth.some((route) => window.location.href.includes(route))) {
      redirectToSignIn();
    }
  },

  async refetchUserInfo({ commit }) {
    await fetchAndCommitUsersData(commit);
  },

  async initFetch({ commit, state }) {
    commit('setLoading', true);

    const isRedirectTo = await fetchAndCommitUsersData(commit);

    addClarityCustomTags(state.userInfo);

    if (isRedirectTo) {
      commit('setLoading', false);
      hideAllAnimations();
      return;
    }

    await fetchAndCommitCountriesData(commit);
    await fetchAndCommitUserPrefsData(commit);
    commit('incrementInitialDataLoaded');

    commit('setLoading', false);

    hideAllAnimations();
  },

  showNotification({ commit }, notificationData: Array<NotificationData>) {
    commit('setNotification', true);
    commit('setNotificationData', notificationData);
  },
};

export default actions;
